import { Component, ViewChild, ElementRef, Input, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ModalController } from '@ionic/angular';
import { IImageSearchResult } from 'src/app/models/google/image-search-result.interface';
import { IGoogleSearchResult } from 'src/app/models/google/search-result.interface';
import { PostService } from 'src/app/services/post.service';
import { CreateSupportTicketModalComponent } from '../create-support-ticket-modal/create-support-ticket-modal.component';
import { IPostUpdate } from 'src/app/models/post-update.interface';

@Component({
    selector: 'app-image-selection-modal',
    templateUrl: './image-selection-modal.component.html',
    styleUrls: ['./image-selection-modal.component.scss'],
    standalone: false
})
export class ImageSelectionModalComponent implements OnInit {

  @ViewChild('fileInput') 
  fileInput: ElementRef<HTMLInputElement>;

  @Input() defaultSearchQuery: string = '';
  @Input() post: IPostUpdate;
  @Input() images: string[] = []; // Changed from public property to Input

  public imageSizes: { [key: string]: { width: number; height: number } } = {};

  uploadedImageUrl: string | ArrayBuffer | null;
  searchResults: IGoogleSearchResult[] = [];

  selectedTab: string = 'add';
  url: string;
  searchQuery: string = '';

  public selectedFilter: string = 'All'; 

  isCapturing: boolean = false;
  isSearching: boolean = false;
  isVerifyingSelection: boolean = false;
  showFilterPopover: boolean = false;
  selectionErrorMessage: string = '';

  timeFilters = ['All','1 day','1 week','1 month','1 year'];

  constructor(private modalController: ModalController,
              private postService: PostService) { }



  ngOnInit(): void {
    // Set the search query to the defaultSearchQuery if provided
    if (this.defaultSearchQuery) {
      this.searchQuery = this.defaultSearchQuery;
    }
    // Always start on merged tab now
    this.selectedTab = 'add';
  }


  /**
   * Returns true when the provided query looks like a direct image URL.
   */
  isDirectImageUrl(query: string | undefined | null): boolean {
    if (!query) return false;
    const trimmed = query.trim();
    // Basic check: http(s) URL that ends with a common image extension (possibly with query string)
    const re = /^https?:.*\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i;
    return re.test(trimmed);
  }

  /**
   * Getter used by the template to determine whether to show Accept UI.
   */
  get inputIsImageUrl(): boolean {
    return this.isDirectImageUrl(this.searchQuery);
  }

  /**
   * Handler for Enter key on the search input: accept direct image URLs, otherwise search.
   */
  onEnter() {
    if (this.inputIsImageUrl) {
      this.acceptSearchUrl();
      return;
    }
    this.searchImages();
  }

  /**
   * Accept the searchQuery as a direct image URL and return it as the selected image.
   */
  acceptSearchUrl() {
    const url = this.searchQuery?.trim();
    if (!url) {
      return;
    }
    // Add to images list if not present
    if (!this.images.includes(url)) {
      this.images.unshift(url);
    }
    this.verifyAndSelectImage(url);
  }



  /**
   * 
   */
  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }


  /**
   * 
   * @param event 
   */
  uploadImage(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e: any) => {
      this.uploadedImageUrl = e.target.result;
      // Assuming you want to add the uploaded image to the selection array
      if (typeof e.target.result === 'string') {
        const imageUrl = e.target.result;
        this.images.unshift(imageUrl);          
        this.modalController.dismiss({
            'selectedImage': imageUrl,
            'file': file
        });
      }
    };
    reader.readAsDataURL(file);
  }


  onImageLoaded(imageUrl: string, event: any) {
    const img: HTMLImageElement = event.target as HTMLImageElement;
    const width = img.naturalWidth;
    const height = img.naturalHeight;

    this.imageSizes[imageUrl] = { width, height };
  }


  
  async presentFilterPopover(ev: Event) {
    // Toggle popover flag. You can further enhance this by using IonPopoverController if you need a separate component.
    this.showFilterPopover = true;
  }

  dismissFilterPopover(ev: any) {
    // When popover is dismissed, set the flag off. You may use the event data if needed.
    this.showFilterPopover = false;
  }

  applyFilter() {
    // Close popover (if using manual flag control) and optionally trigger a search
    this.showFilterPopover = false;
    // Optionally, you can call searchImages() immediately after filter selection.
  }
  

  /**
   * Validate remote image URLs before selecting to avoid publish-time failures.
   */
  selectImage(imageUrl: string) {
    this.verifyAndSelectImage(imageUrl);
  }


  /**
   * Finalize image selection and close the modal.
   */
  private dismissWithSelectedImage(imageUrl: string) {
    this.modalController.dismiss({
      'selectedImage': imageUrl,
      'images': this.images
    });
  }


  /**
   * Verify URL can be downloaded by the server before allowing selection.
   */
  private verifyAndSelectImage(imageUrl: string) {
    const trimmedUrl = imageUrl?.trim();
    if (!trimmedUrl || this.isVerifyingSelection) {
      return;
    }

    this.selectionErrorMessage = '';

    if (!this.isHttpUrl(trimmedUrl)) {
      this.dismissWithSelectedImage(trimmedUrl);
      return;
    }

    this.isVerifyingSelection = true;
    this.postService.verifyImageDownloadable(trimmedUrl).subscribe({
      next: (result) => {
        this.isVerifyingSelection = false;
        if (result.downloadable) {
          this.dismissWithSelectedImage(trimmedUrl);
          return;
        }

        this.selectionErrorMessage = result.message
          || 'This image URL cannot be downloaded by the server. Choose another image or upload one.';
      },
      error: (error: HttpErrorResponse) => {
        this.isVerifyingSelection = false;
        this.selectionErrorMessage = error.error?.message
          || 'Unable to verify this image right now. Try another image or upload one.';
      }
    });
  }


  /**
   * Return true for URLs that require server-side download verification.
   */
  private isHttpUrl(value: string): boolean {
    return /^https?:\/\//i.test(value);
  }


  searchImages() {
    if (!this.searchQuery) return;
    this.isSearching = true;
    this.postService.searchImages(this.searchQuery, this.selectedFilter).subscribe({
      next: (results: IImageSearchResult) => {
        this.searchResults = results.images;

        // Add new image URLs to images list and avoid duplicates
        const newImageUrls = results.images.map(result => result.link);

        this.images = [...new Set([...newImageUrls, ...this.images])];
        
        this.isSearching = false;
      },
      error: (error) => {
        console.error('Error searching for images:', error);
        this.isSearching = false;
      }
    });
  }


    /**
   * Clear the selected image (sets the selection to null)
   */
  clearImage() {
    this.modalController.dismiss({
      'selectedImage': null
    });
  }


  captureScreenshot() {
    if (this.url) {
      this.isCapturing = true;
      this.postService.captureScreenshot(this.url).subscribe({
        next: (imageUrl) => {
          // Handle the result, e.g., show the image or notify the user
          this.images.unshift(imageUrl);    
          this.isCapturing = false;
          this.modalController.dismiss({
            'selectedImage': imageUrl
          });
        },
        error: (error) => {
          console.error('Error capturing screenshot:', error);
          this.isCapturing = false;
        }
      });
    }
  }

  /**
   * 
   */
    createSupportTicket() {
      this.openCreateTicketModal(null);
    }
  
  
    /**
     * 
     * @param logId 
     */
    async openCreateTicketModal(logId: string) {
      const modal = await this.modalController.create({
        component: CreateSupportTicketModalComponent,
        componentProps: { abstract: `Capture modification for '${this.url}'`, logId },
      });
  
      modal.onDidDismiss().then((data) => {
        if (data?.data?.ticket) {
          console.log('Ticket created:', data.data.ticket);
        }
      });
  
      await modal.present();
    }  


  dismissModal() {
    this.modalController.dismiss({
      'images': this.images
    });
  }
}
